import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
  Avatar,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getDefaultHomePath } from '../../types/auth';

// Mapeo de rutas a nombres para las migas de pan
const routeNameMap: Record<string, string> = {
  'dashboard': 'Sincronización ComPsych',
  'accounts': 'Cuentas',
  'clients': 'Clientes',
  'referrals': 'Casos',
  'claims': 'Citas',
  'new-entry': 'Nueva Captura',
  'settings': 'Configuración',
  'users': 'Gestión de Usuarios', // ✅ NUEVO
  'survey-templates': 'Plantillas de Encuesta',
  'client': 'Detalle de Cliente',
  'referral': 'Detalle de Caso',
  'claim': 'Detalle de Cita'
};

interface NavbarProps {
  onDrawerToggle: () => void;
  onDesktopToggle: () => void;
  desktopOpen: boolean;
  drawerWidth: number;
}

const Navbar: React.FC<NavbarProps> = ({ onDrawerToggle, onDesktopToggle, desktopOpen, drawerWidth }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  
  // Procesar la ruta actual para las migas de pan
  const pathnames = location.pathname.split('/').filter(x => x);
  
  // ✅ CORREGIDO: Obtener iniciales del usuario para el avatar
  const getUserInitials = (): string => {
    if (!user || !user.fullName) return '?';
    return user.fullName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Obtener el nombre del entorno con color
  const getEnvironmentChip = () => {
    const env = import.meta.env.VITE_ENVIRONMENT;
    if (env === 'development') {
      return (
        <Chip 
          label="DEV" 
          size="small" 
          color="warning" 
          variant="outlined"
          sx={{ ml: 2, fontSize: '0.75rem' }}
        />
      );
    }
    if (env === 'production') {
      return (
        <Chip 
          label="PROD" 
          size="small" 
          color="success" 
          variant="outlined"
          sx={{ ml: 2, fontSize: '0.75rem' }}
        />
      );
    }
    return null;
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        backgroundColor: 'background.paper',
        color: 'text.primary',
        boxShadow: 1,
        transition: (theme) => theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onDrawerToggle}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        
        {/* Botón toggle para desktop */}
        <IconButton
          color="inherit"
          aria-label="toggle drawer"
          edge="start"
          onClick={onDesktopToggle}
          sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
        >
          {desktopOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
        
        {/* Breadcrumbs */}
        <Box sx={{ flexGrow: 1 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link 
              component={RouterLink} 
              to={user ? getDefaultHomePath(user) : '/dashboard'} 
              color="inherit"
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                eap
              </Typography>
            </Link>
            
            {pathnames.map((value, index) => {
              const last = index === pathnames.length - 1;
              const to = `/${pathnames.slice(0, index + 1).join('/')}`;
              const name = routeNameMap[value] || value;

              // Si es un ID (es numérico), no lo mostramos como un enlace
              if (!isNaN(Number(value))) {
                return null;
              }
              
              return last ? (
                <Typography key={to} variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                  {name}
                </Typography>
              ) : (
                <Link
                  key={to}
                  component={RouterLink}
                  underline="hover"
                  color="inherit"
                  to={to}
                >
                  <Typography variant="body2">
                    {name}
                  </Typography>
                </Link>
              );
            })}
          </Breadcrumbs>
          
          {/* Indicador de entorno */}
          {getEnvironmentChip()}
        </Box>
        
        {/* Información del usuario y botón de logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* ✅ CORREGIDO: Usar user.fullName en lugar de user directamente */}
          <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
            Bienvenido, <strong>{user?.fullName || 'Usuario'}</strong>
          </Typography>
          
          <Avatar 
            sx={{ 
              bgcolor: 'primary.main', 
              width: 32, 
              height: 32,
              fontSize: '0.875rem',
              fontWeight: 'bold'
            }}
          >
            {getUserInitials()}
          </Avatar>
          
          <Button 
            color="inherit" 
            onClick={logout}
            startIcon={<LogoutIcon />}
            size="small"
            sx={{ 
              ml: 1,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Salir
            </Box>
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;